if (!customElements.get('build-your-own-bundle')) {
  customElements.define(
    'build-your-own-bundle',
    class BuildYourOwnBundle extends HTMLElement {
      connectedCallback() {
        if (this.isReady) return;

        const configElement = this.querySelector('[data-byob-config]');
        if (!configElement) return;

        this.config = JSON.parse(configElement.textContent);
        this.config.tiers = (this.config.tiers || [])
          .map((tier) => ({
            quantity: Number(tier.quantity) || 0,
            percent: Number(tier.percent) || 0,
          }))
          .filter((tier) => tier.quantity > 0 && tier.percent > 0)
          .sort((left, right) => left.quantity - right.quantity);

        this.cards = Array.from(this.querySelectorAll('[data-byob-card]'));
        this.filtersElement = this.querySelector('[data-byob-filters]');
        this.filterEmptyElement = this.querySelector('[data-byob-filter-empty]');
        this.routineNote = this.querySelector('[data-byob-routine-note]');
        this.activeType = '';
        this.routineConcerns = null;
        this.activeRoutineMoment = '';
        this.searchQuery = '';
        this.summaryItems = this.querySelector('[data-byob-summary-items]');
        this.summaryMain = this.querySelector('.byob-summary__main');
        this.summary = this.querySelector('.byob-summary');
        this.countElement = this.querySelector('[data-byob-count]');
        this.discountElement = this.querySelector('[data-byob-discount]');
        this.emptyState = this.querySelector('[data-byob-empty]');
        this.tierList = this.querySelector('[data-byob-tier-list]');
        this.progress = this.querySelector('[data-byob-progress]');
        this.meter = this.querySelector('[data-byob-meter]');
        this.meterFill = this.querySelector('[data-byob-meter-fill]');
        this.starter = this.querySelector('[data-byob-starter]');
        this.starterList = this.querySelector('[data-byob-starter-list]');
        this.starterButton = this.querySelector('[data-byob-starter-add]');
        this.starterTitle = this.querySelector('[data-byob-starter-title]');
        this.starterCopy = this.querySelector('[data-byob-starter-copy]');
        this.subscriptionSavingsElement = this.querySelector('[data-byob-subscription-savings]');
        this.subscriptionLabelElement = this.querySelector('[data-byob-subscription-label]');
        this.subscriptionAmountElement = this.querySelector('[data-byob-subscription-amount]');
        this.bundleLabelElement = this.querySelector('[data-byob-bundle-label]');
        this.savingsElement = this.querySelector('[data-byob-savings]');
        this.totalCompareElement = this.querySelector('[data-byob-total-compare]');
        this.totalElement = this.querySelector('[data-byob-total]');
        this.shippingElement = this.querySelector('[data-byob-shipping]');
        this.dock = this.querySelector('[data-byob-dock]');
        this.dockToggle = this.querySelector('[data-byob-dock-toggle]');
        this.sheet = this.querySelector('[data-byob-sheet]');
        this.dockOffer = this.querySelector('[data-byob-dock-offer]');
        this.dockCompare = this.querySelector('[data-byob-dock-compare]');
        this.dockTotal = this.querySelector('[data-byob-dock-total]');
        this.dockNudge = this.querySelector('[data-byob-dock-nudge]');
        this.dockShipping = this.querySelector('[data-byob-dock-shipping]');
        this.statusElement = this.querySelector('[data-byob-status]');
        this.submitButtons = Array.from(this.querySelectorAll('[data-byob-submit]'));
        this.onceButton = this.querySelector('[data-byob-submit-once]');
        this.subscribeNote = this.querySelector('[data-byob-subscribe]');
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.items = new Map();
        this.cardState = new WeakMap();
        this.searchText = new WeakMap();
        this.selectedProductIds = new Set();
        this.isSubmitting = false;
        this.isLoadingMore = false;
        this.isLoadingCollection = false;
        this.isReady = true;
        this.pageRequest = null;
        this.searchTimer = 0;
        this.announcedTier = null;
        this.starterCards = [];
        this.otherCartTotal = 0;
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (this.isSubmitting || !event?.cartData?.items) return;
            this.rememberOtherCartTotal(event.cartData);
            this.render({ cards: false });
          });
        }

        this.sheet?.addEventListener('click', (event) => {
          if (event.target === this.sheet) this.sheet.close();
        });
        this.sheet?.addEventListener('close', () => this.syncSheetToggle());
        this.sheetMedia = window.matchMedia('(min-width: 990px)');
        this.sheetMedia.addEventListener('change', () => {
          if (this.sheetMedia.matches) this.sheet?.close();
        });
        this.searchBar = this.querySelector('.byob-search');
        this.searchMedia = window.matchMedia('(max-width: 989px)');
        const header = document.querySelector('.header-container') || document.querySelector('.section-header');
        if (header) {
          this.searchHeaderObserver = new MutationObserver(() => this.trackSearchPin());
          this.searchHeaderObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
        }
        this.pinSearch();
        this.onSearchResize = () => this.pinSearch();
        this.searchMedia.addEventListener('change', this.onSearchResize);
        window.addEventListener('resize', this.onSearchResize);
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onSearch.bind(this));
        this.summaryMain?.addEventListener('scroll', () => this.scheduleScrollHint(), { passive: true });
        this.summaryItems?.addEventListener('scroll', () => this.scheduleScrollHint(), { passive: true });
        if (window.ResizeObserver && this.summaryMain) {
          this.scrollHintObserver = new ResizeObserver(() => this.scheduleScrollHint());
          this.scrollHintObserver.observe(this.summaryMain);
          if (this.summaryItems) this.scrollHintObserver.observe(this.summaryItems);
        }
        this.setupStarter();
        this.addHealthInterestFilters(this.cards);
        this.restoreSelection();
        if (this.appendBundleId()) this.cartBundlePending = true;
        this.render(this.items.size ? {} : { cards: false });
        this.prepareBundle();
      }

      async prepareBundle() {
        await this.addRequestedProduct();
        if (this.appendBundleId()) {
          await this.hydrateFromCartBundle();
          return;
        }
        await this.dropItemsAlreadyInCart();
      }

      async dropItemsAlreadyInCart() {
        try {
          const currentCart = await fetch('/cart.js').then((response) => response.json());
          this.rememberOtherCartTotal(currentCart);
          const existing = new Set(
            (currentCart.items || [])
              .filter((item) => String(item.properties?._byob_bundle_id || '').startsWith('byob-'))
              .map((item) => String(item.variant_id))
          );
          if (!existing.size) {
            if (this.otherCartTotal > 0) this.render();
            return;
          }

          let removed = false;
          for (const [key, item] of this.items) {
            if (!existing.has(String(item.variantId))) continue;
            this.items.delete(key);
            removed = true;
          }
          if (removed || this.otherCartTotal > 0) this.render();
        } catch (error) {
          console.error(error);
        }
      }

      async findCard(productId) {
        let card = this.cards.find((entry) => entry.dataset.productId === productId);
        let button = this.querySelector('[data-byob-more]');
        while (!card && button) {
          try {
            await this.fetchNextPage(button);
          } catch (error) {
            break;
          }
          card = this.cards.find((entry) => entry.dataset.productId === productId);
          button = this.querySelector('[data-byob-more]');
        }
        return card;
      }

      async hydrateFromCartBundle() {
        const bundleId = this.appendBundleId();
        if (!bundleId) return;

        try {
          const currentCart = await fetch('/cart.js').then((response) => response.json());
          this.rememberOtherCartTotal(currentCart);
          const lines = (currentCart.items || []).filter((item) => item.properties?._byob_bundle_id === bundleId);
          this.cartBundleVariantIds = new Set(lines.map((item) => String(item.variant_id)));
          this.cartBundleSubscription = lines.some((item) => item.selling_plan_allocation);

          for (const line of lines) {
            const productId = String(line.product_id);
            const card = await this.findCard(productId);
            const select = card?.querySelector('[data-byob-variant-select]');
            const variantId = String(line.variant_id);
            if (!select || ![...select.options].some((option) => option.value === variantId)) continue;

            for (const [key, item] of this.items) {
              if (item.productId === productId) this.items.delete(key);
            }
            select.disabled = false;
            select.value = variantId;
            const fresh = this.getSelectedVariant(card);
            if (fresh) this.items.set(fresh.key, { ...fresh, quantity: 1 });
          }
        } catch (error) {
          console.error(error);
        } finally {
          this.cartBundlePending = false;
          this.render();
        }
      }

      async addRequestedProduct() {
        const url = new URL(window.location.href);
        const productId = url.searchParams.get('byob_product');
        if (!productId) return;

        let card = this.cards.find((entry) => entry.dataset.productId === productId);
        let button = this.querySelector('[data-byob-more]');
        while (!card && button) {
          try {
            await this.fetchNextPage(button);
          } catch (error) {
            break;
          }
          card = this.cards.find((entry) => entry.dataset.productId === productId);
          button = this.querySelector('[data-byob-more]');
        }

        const variantId = url.searchParams.get('byob_variant');
        url.searchParams.delete('byob_product');
        url.searchParams.delete('byob_variant');
        window.history.replaceState({}, '', url.toString());
        if (!card) return;

        const select = card.querySelector('[data-byob-variant-select]');
        const variantMatches = !variantId || [...(select?.options || [])].some((option) => option.value === variantId);
        if (!variantMatches) return;

        if (variantId && select && select.value !== variantId) {
          for (const [key, item] of this.items) {
            if (item.productId === productId) this.items.delete(key);
          }
          this.syncSelection();
          select.disabled = false;
          select.value = variantId;
        }

        if (!this.productIsSelected(productId)) this.changeCardQuantity(card, 1);
      }

      restoreSelection() {
        let saved = [];
        try {
          saved = JSON.parse(sessionStorage.getItem('byob-bundle-v1') || '[]');
        } catch (error) {
          return;
        }
        if (!Array.isArray(saved)) return;

        saved.forEach((item) => {
          const variantId = Number(item?.variantId);
          const productId = String(item?.productId || '');
          if (!variantId || !productId) return;

          const card = this.cards.find((entry) => entry.dataset.productId === productId);
          const select = card?.querySelector('[data-byob-variant-select]');
          if (select && [...select.options].some((option) => Number(option.value) === variantId)) {
            select.value = String(variantId);
            const fresh = this.getSelectedVariant(card);
            if (fresh) {
              this.items.set(fresh.key, { ...fresh, quantity: 1 });
              return;
            }
          }

          this.items.set(String(item.key || variantId), {
            ...item,
            key: String(item.key || variantId),
            variantId,
            productId,
            quantity: 1,
            price: Number(item.price) || 0,
            subscriptionPrice: Number(item.subscriptionPrice) || Number(item.price) || 0,
          });
        });
      }

      persistSelection() {
        try {
          if (!this.items.size) {
            sessionStorage.removeItem('byob-bundle-v1');
            return;
          }
          sessionStorage.setItem('byob-bundle-v1', JSON.stringify(Array.from(this.items.values())));
        } catch (error) {
          // Private browsing can block storage. The bundle still works for this page view.
        }
      }

      scheduleScrollHint() {
        if (this.scrollHintFrame) return;
        this.scrollHintFrame = window.requestAnimationFrame(() => {
          this.scrollHintFrame = 0;
          this.updateScrollHint();
        });
      }

      updateScrollHint() {
        if (!this.summary) return;
        const canScroll = (element) => {
          if (!element || element.hidden) return false;
          return element.scrollHeight - element.clientHeight - element.scrollTop > 1;
        };
        this.summary.classList.toggle('is-scrollable', canScroll(this.summaryMain) || canScroll(this.summaryItems));
      }

      onSearch(event) {
        if (!event.target.matches('[data-byob-search]')) return;
        this.searchQuery = event.target.value || '';
        window.clearTimeout(this.searchTimer);
        this.applyFilter();
        if (!this.searchQuery.trim() || !this.querySelector('[data-byob-more]')) return;
        this.searchTimer = window.setTimeout(() => {
          if (this.searchQuery.trim() && this.querySelector('[data-byob-more]')) this.loadEntireCollection();
        }, 250);
      }

      onChange(event) {
        if (event.target.matches('[data-byob-interest]')) {
          const select = event.target;
          this.selectFilter(select.value, select.selectedOptions[0]?.textContent?.trim() || '');
          return;
        }
        if (!event.target.matches('[data-byob-variant-select]')) return;
        const card = event.target.closest('[data-byob-card]');
        if (!card) return;
        this.syncSelection();
        this.renderCard(card, this.activeBundlePercent());
      }

      onClick(event) {
        const starterButton = event.target.closest('[data-byob-starter-add]');
        const routineAdd = event.target.closest('[data-byob-routine-add]');
        const routineSwap = event.target.closest('[data-byob-routine-swap]');
        const moreButton = event.target.closest('[data-byob-more]');
        const addButton = event.target.closest('[data-byob-add]');
        const quantityButton = event.target.closest('[data-byob-qty-action]');
        const removeButton = event.target.closest('[data-byob-remove]');
        const sheetClose = event.target.closest('[data-byob-sheet-close]');
        const dockToggle = event.target.closest('[data-byob-dock-toggle]');
        const onceButton = event.target.closest('[data-byob-submit-once]');
        const submitButton = event.target.closest('[data-byob-submit]');

        if (sheetClose) {
          this.sheet?.close();
          return;
        }

        if (dockToggle) {
          this.toggleSheet();
          return;
        }

        if (starterButton) {
          this.addStarterSet();
          return;
        }

        if (routineAdd) {
          this.addRoutineProduct(routineAdd.closest('[data-byob-routine-moment]'));
          return;
        }

        if (routineSwap) {
          this.swapRoutineMoment(routineSwap.closest('[data-byob-routine-moment]'));
          return;
        }

        const reorderButton = event.target.closest('[data-byob-reorder]');
        if (reorderButton) {
          this.addReorder(reorderButton);
          return;
        }

        if (moreButton) {
          this.loadMore(moreButton);
          return;
        }

        if (addButton) {
          this.changeCardQuantity(addButton.closest('[data-byob-card]'), 1);
        } else if (quantityButton) {
          const direction = quantityButton.dataset.byobQtyAction === 'increase' ? 1 : -1;
          const card = quantityButton.closest('[data-byob-card]');
          const summaryItem = quantityButton.closest('[data-byob-key]');
          if (card) {
            this.changeCardQuantity(card, direction);
          } else if (summaryItem) {
            this.changeQuantity(summaryItem.dataset.byobKey, direction);
          }
        } else if (removeButton) {
          const summaryItem = removeButton.closest('[data-byob-key]');
          const card = removeButton.closest('[data-byob-card]');
          if (summaryItem) {
            const item = this.items.get(summaryItem.dataset.byobKey);
            if (this.isInCartBundle(item?.variantId)) return;
            this.items.delete(summaryItem.dataset.byobKey);
          } else if (card?.dataset.productId) {
            if (this.productIsInCartBundle(card.dataset.productId)) return;
            for (const [key, item] of this.items) {
              if (item.productId === card.dataset.productId) this.items.delete(key);
            }
          }
          this.render();
        } else if (onceButton) {
          this.submitBundle('once');
        } else if (submitButton) {
          this.submitBundle('subscription');
        }
      }

      getSelectedVariant(card) {
        const select = card.querySelector('[data-byob-variant-select]');
        const option = select?.selectedOptions?.[0];
        if (!option) return null;

        const variantTitle = option.dataset.variantTitle || '';
        const variantSize = option.dataset.variantSize || '';
        const inventoryQuantity = Number(option.dataset.inventoryQuantity);
        const tracksInventory = option.dataset.inventoryManagement === 'shopify' && option.dataset.inventoryPolicy !== 'continue';

        return {
          key: String(option.value),
          variantId: Number(option.value),
          productId: card.dataset.productId || '',
          productTitle: card.dataset.productTitle || '',
          variantTitle,
          variantSize,
          price: Number(option.dataset.price || 0),
          subscriptionPrice: Number(option.dataset.subscriptionPrice || option.dataset.price || 0),
          sellingPlanId: option.dataset.sellingPlanId || '',
          sellingPlanName: option.dataset.sellingPlanName || '',
          image: option.dataset.image || card.dataset.productImage || '',
          available: option.dataset.available === 'true',
          maxQuantity: tracksInventory && Number.isFinite(inventoryQuantity) ? inventoryQuantity : null,
        };
      }

      changeCardQuantity(card, delta) {
        const variant = card ? this.getSelectedVariant(card) : null;
        if (!variant || !variant.available) return;
        if (delta > 0 && this.productIsSelected(variant.productId)) return;
        const moment = this.activeRoutineMoment || this.momentLabelForProduct(variant.productId) || '';
        this.changeQuantity(variant.key, delta, { ...variant, moment });
      }

      momentLabelForProduct(productId) {
        if (!productId) return '';
        const slot = this.querySelector(`[data-byob-routine-moment][data-product-id="${productId}"]`);
        return slot?.dataset.moment || '';
      }

      cardConcerns(card) {
        return (card.dataset.goodFor || '')
          .split('||')
          .map((concern) => concern.trim())
          .filter(Boolean);
      }

      cardHealthInterests(card) {
        return (card.dataset.healthInterest || '')
          .split('||')
          .map((interest) => interest.trim())
          .filter(Boolean);
      }

      cardMatchesConcern(card) {
        const concerns = this.cardConcerns(card).map((concern) => concern.toLowerCase());
        if (this.routineConcerns?.length) {
          return this.routineConcerns.some((wanted) => concerns.includes(wanted));
        }
        if (!this.activeType) return true;
        const needle = this.activeType.toLowerCase();
        return this.cardHealthInterests(card).some((interest) => interest.toLowerCase() === needle);
      }

      showRoutineNote(message) {
        if (!this.routineNote) return;
        this.routineNote.hidden = !message;
        this.routineNote.textContent = message || '';
      }

      async addReorder(button) {
        const productId = button?.dataset.productId || '';
        if (!productId || button.disabled || this.productIsSelected(productId)) return;

        const title = button.dataset.productTitle || 'product';
        button.disabled = true;
        button.textContent = 'Adding...';
        const card = await this.findCard(productId);
        if (!card) {
          button.disabled = false;
          button.textContent = 'Add to bundle';
          this.setStatus('That product is not available in this bundle.');
          return;
        }

        const variantId = button.dataset.variantId || '';
        const select = card.querySelector('[data-byob-variant-select]');
        if (variantId && select && [...select.options].some((option) => option.value === variantId)) {
          select.value = variantId;
        }
        this.changeCardQuantity(card, 1);
        if (!this.productIsSelected(productId)) {
          button.disabled = false;
          button.textContent = 'Add to bundle';
          button.setAttribute('aria-label', `Add ${title} to bundle`);
        }
      }

      renderReorder() {
        const offer = this.catalogSubscriptionPercent();
        const text = this.querySelector('.byob-reorder__text');
        if (text && offer > 0) {
          text.textContent = `Subscribe to the products you already use, and save ${offer}% each time they arrive.`;
        }

        this.querySelectorAll('[data-byob-reorder]').forEach((button) => {
          const title = button.dataset.productTitle || 'product';
          const added = this.productIsSelected(button.dataset.productId || '');
          button.disabled = added;
          button.textContent = added ? 'Added' : 'Add to bundle';
          button.setAttribute('aria-label', added ? `${title} added to bundle` : `Add ${title} to bundle`);
        });
      }

      async addRoutineProduct(slot) {
        if (!slot?.dataset.productId) return;
        const card = await this.findCard(slot.dataset.productId);
        if (!card) {
          this.setStatus('That product is not available in this bundle.');
          return;
        }
        this.activeRoutineMoment = slot.dataset.moment || '';
        this.changeCardQuantity(card, 1);
        this.activeRoutineMoment = '';
      }

      swapRoutineMoment(slot) {
        if (!slot) return;
        const concerns = (slot.dataset.goodFor || '')
          .split('||')
          .map((concern) => concern.trim().toLowerCase())
          .filter(Boolean);
        if (!concerns.length) return;

        this.activeType = '';
        this.routineConcerns = concerns;
        this.activeRoutineMoment = slot.dataset.moment || '';
        const productTitle = slot.querySelector('.byob-routine__product')?.textContent?.trim() || 'this product';
        const moment = slot.dataset.moment || 'This step';
        this.showRoutineNote(`${moment}: other products with the same kind of support as ${productTitle}.`);
        this.applyFilter();
        if (this.querySelector('[data-byob-more]')) this.loadEntireCollection();
        const grid = this.querySelector('[data-byob-filter-loading]:not([hidden])') || this.querySelector('.byob__grid');
        const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        grid?.scrollIntoView({ behavior: motion, block: 'start' });
      }

      renderRoutine() {
        const filledMoments = new Set(
          Array.from(this.items.values(), (item) => item.moment || this.momentLabelForProduct(item.productId)).filter(Boolean)
        );

        this.querySelectorAll('[data-byob-routine-moment]').forEach((slot) => {
          const added = this.productIsSelected(slot.dataset.productId || '');
          slot.classList.toggle('is-added', added || filledMoments.has(slot.dataset.moment || ''));
          const button = slot.querySelector('[data-byob-routine-add]');
          const routineTitle = slot.querySelector('.byob-routine__product')?.textContent?.trim() || 'product';
          if (button) {
            button.disabled = added;
            button.textContent = added ? 'Added' : 'Add';
            button.setAttribute('aria-label', added ? `${routineTitle} added` : `Add ${routineTitle}`);
          }
          const card = this.cards.find((entry) => entry.dataset.productId === slot.dataset.productId);
          const cardPrice = card?.querySelector('[data-byob-price]');
          const slotPrice = slot.querySelector('[data-byob-routine-price]');
          if (cardPrice && slotPrice) slotPrice.textContent = cardPrice.textContent.trim();
        });
      }

      productIsSelected(productId) {
        if (!productId) return false;
        if (this.selectedProductIds) return this.selectedProductIds.has(productId);
        return Array.from(this.items.values()).some((item) => item.productId === productId);
      }

      syncSelection() {
        this.selectedProductIds = new Set();
        for (const item of this.items.values()) this.selectedProductIds.add(item.productId);
      }

      changeQuantity(key, delta, variant) {
        const current = this.items.get(key);
        const nextQuantity = (current?.quantity || 0) + delta;
        const source = variant || current;
        if (!source) return;

        if (nextQuantity <= 0) {
          this.items.delete(key);
        } else if (nextQuantity > 1 || (source.productId && this.productIsSelected(source.productId) && !current)) {
          return;
        } else if (source.maxQuantity != null && nextQuantity > source.maxQuantity) {
          this.setStatus(`Only ${source.maxQuantity} available.`);
          return;
        } else {
          this.items.set(key, { ...source, quantity: 1 });
        }

        this.setStatus('');
        this.render();
      }

      getTotalQuantity() {
        return Array.from(this.items.values()).reduce((total, item) => total + item.quantity, 0);
      }

      getRetailSubtotal() {
        return Array.from(this.items.values()).reduce((total, item) => total + (item.price || 0) * item.quantity, 0);
      }

      getSubtotal() {
        return Array.from(this.items.values()).reduce((total, item) => total + this.basePrice(item) * item.quantity, 0);
      }

      basePrice(item) {
        const subscriptionPrice = Number(item?.subscriptionPrice);
        if (item?.sellingPlanId && subscriptionPrice > 0 && subscriptionPrice < item.price) return subscriptionPrice;
        return item?.price || 0;
      }

      applyPercent(cents, percent) {
        if (!(percent > 0)) return cents;
        // Shopify drops the fractional cent when it allocates a percentage discount.
        const discount = Math.floor((cents * percent) / 100);
        return Math.max(0, cents - discount);
      }

      getPurchaseTotal(percent, mode) {
        return Array.from(this.items.values()).reduce((total, item) => {
          const base = mode === 'once' ? item.price : this.basePrice(item);
          return total + this.applyPercent(base, percent) * item.quantity;
        }, 0);
      }

      bundleDiscountsEnabled() {
        return this.config.bundleDiscountEnabled === true;
      }

      activeBundlePercent(quantity = this.getTotalQuantity()) {
        if (!this.bundleDiscountsEnabled()) return 0;
        const tier = this.getCurrentTier(quantity);
        return tier ? tier.percent : 0;
      }

      getCurrentTier(quantity = this.getTotalQuantity()) {
        return this.config.tiers.reduce((matched, tier) => (quantity >= tier.quantity ? tier : matched), null);
      }

      getNextTier(quantity = this.getTotalQuantity()) {
        return this.config.tiers.find((tier) => quantity < tier.quantity) || null;
      }

      render({ cards = true, filter = false } = {}) {
        const quantity = this.getTotalQuantity();
        const tier = this.bundleDiscountsEnabled() ? this.getCurrentTier(quantity) : null;
        const nextTier = this.bundleDiscountsEnabled() ? this.getNextTier(quantity) : null;
        const percent = this.activeBundlePercent(quantity);
        const retailSubtotal = this.getRetailSubtotal();
        const subscriptionSubtotal = this.getSubtotal();
        const subscriptionTotal = this.getPurchaseTotal(percent, 'subscription');
        const onceTotal = this.getPurchaseTotal(percent, 'once');
        const bundleSavings = subscriptionSubtotal - subscriptionTotal;
        const subscriptionSavings = retailSubtotal - subscriptionSubtotal;
        const minimumItems = Number(this.config.minimumItems || 0);
        const appending = Boolean(this.appendBundleId() && this.cartBundleVariantIds);
        const canSubmit = this.cartBundlePending
          ? false
          : appending
            ? this.newBundleItems().length > 0 && !this.isSubmitting
            : quantity >= minimumItems && !this.isSubmitting;

        this.syncSelection();
        const subscriptionPercent = quantity > 0
          ? this.subscriptionDiscountPercent()
          : this.catalogSubscriptionPercent();
        this.renderTiers(quantity, tier, subscriptionPercent);
        this.renderMeter(quantity, nextTier, subscriptionPercent);
        this.renderProgress(quantity, tier, nextTier, minimumItems, subscriptionPercent);
        this.renderHeading(quantity, percent, subscriptionPercent);
        this.renderSummary(percent);
        const estimatedTotal = subscriptionTotal;
        const showCompare = retailSubtotal > estimatedTotal;
        const totalSavings = subscriptionSavings + bundleSavings;
        const showSavings = this.bundleDiscountsEnabled() ? percent > 0 && totalSavings > 0 : totalSavings > 0;
        if (this.subscriptionSavingsElement) this.subscriptionSavingsElement.hidden = true;
        if (this.bundleLabelElement) {
          this.bundleLabelElement.textContent = 'Amount saved';
        }
        this.renderMoney(this.savingsElement, totalSavings);
        const savingsRow = this.savingsElement?.parentElement;
        if (savingsRow) savingsRow.hidden = !showSavings;
        this.renderMoney(this.totalCompareElement, retailSubtotal);
        this.renderMoney(this.totalElement, estimatedTotal);
        if (this.totalCompareElement) this.totalCompareElement.hidden = !showCompare;
        if (this.totalElement) this.totalElement.classList.toggle('is-sale', showCompare);
        const shipping = this.shippingState(estimatedTotal, quantity, minimumItems);
        if (this.shippingElement) {
          this.shippingElement.hidden = !shipping.visible;
          this.shippingElement.textContent = shipping.text;
        }
        this.renderDock({
          quantity,
          percent,
          nextTier,
          minimumItems,
          subscriptionPercent,
          retailSubtotal,
          estimatedTotal,
          shipping,
        });
        if (cards) this.renderCards(percent);
        this.renderStarter();

        this.submitButtons.forEach((button) => {
          button.disabled = !canSubmit;
          const remaining = Math.max(minimumItems - quantity, 0);
          button.textContent = button.closest('[data-byob-dock]')
            ? this.dockButtonLabel(remaining)
            : this.purchaseButtonLabel(quantity, subscriptionTotal);
        });
        this.renderOnceOption(quantity, onceTotal, canSubmit);
        this.syncDockHeight();
        if (filter) this.applyFilter();
        this.scheduleScrollHint();
        this.renderRoutine();
        this.renderReorder();
        this.persistSelection();
      }

      variantSubscriptionPercent(variant) {
        const price = variant?.price || 0;
        const subscriptionPrice = this.basePrice(variant);
        if (!variant?.sellingPlanId || price <= 0 || subscriptionPrice >= price) return 0;
        return Math.round(((price - subscriptionPrice) / price) * 100);
      }

      subscriptionDiscountPercent() {
        const percents = Array.from(this.items.values()).map((item) => this.variantSubscriptionPercent(item));
        if (!percents.length || percents.some((value) => value !== percents[0])) return 0;
        return percents[0];
      }

      selectFilter(type) {
        this.activeType = type;
        this.routineConcerns = null;
        this.activeRoutineMoment = '';
        this.showRoutineNote('');
        this.applyFilter();
        if (type && this.querySelector('[data-byob-more]')) this.loadEntireCollection();
      }

      showFilterLoading() {
        const moreButton = this.querySelector('[data-byob-more]');
        const loading = this.querySelector('[data-byob-filter-loading]');
        if (moreButton) moreButton.hidden = true;
        if (this.filterEmptyElement && this.isLoadingCollection) this.filterEmptyElement.hidden = true;
        if (loading) loading.hidden = false;
      }

      hideFilterLoading() {
        const grid = this.querySelector('.byob__grid');
        const loading = this.querySelector('[data-byob-filter-loading]');
        if (grid) grid.hidden = false;
        if (loading) loading.hidden = true;
      }

      async loadEntireCollection() {
        if (this.isLoadingCollection) return;
        const moreButton = this.querySelector('[data-byob-more]');
        if (!moreButton) return;

        this.isLoadingCollection = true;
        this.showFilterLoading();

        try {
          let button = moreButton;
          while (button) {
            const hasNext = await this.fetchNextPage(button, { deferRender: true });
            this.applyFilter();
            if (!hasNext) break;
            button = this.querySelector('[data-byob-more]');
          }
        } catch (error) {
          console.error(error);
          const remaining = this.querySelector('[data-byob-more]');
          if (remaining) {
            remaining.hidden = false;
            remaining.disabled = false;
            remaining.textContent = 'View more';
          }
          this.setStatus('There was an error loading more products.');
        } finally {
          this.isLoadingCollection = false;
          this.hideFilterLoading();
          this.applyFilter();
        }
      }

      async loadMore(button) {
        if (this.isLoadingMore || this.isLoadingCollection) return;

        this.isLoadingMore = true;
        button.disabled = true;
        button.textContent = 'Loading...';

        try {
          await this.fetchNextPage(button);
        } catch (error) {
          console.error(error);
          button.disabled = false;
          button.textContent = 'View more';
          this.setStatus('There was an error loading more products.');
        } finally {
          this.isLoadingMore = false;
        }
      }

      async fetchNextPage(button, { deferRender = false } = {}) {
        while (this.pageRequest) await this.pageRequest;

        const request = this.loadPage(button, { deferRender });
        this.pageRequest = request;
        try {
          return await request;
        } finally {
          if (this.pageRequest === request) this.pageRequest = null;
        }
      }

      async loadPage(button, { deferRender = false } = {}) {
        if (!button.dataset.nextUrl || !this.dataset.sectionId) return false;

        const nextUrl = new URL(button.dataset.nextUrl, window.location.href);
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.forEach((value, key) => {
          if (key !== 'page' && key !== 'section_id' && !nextUrl.searchParams.has(key)) {
            nextUrl.searchParams.set(key, value);
          }
        });
        nextUrl.searchParams.set('section_id', this.dataset.sectionId);

        const response = await fetch(nextUrl);
        if (!response.ok) throw new Error(`Unable to load more products (${response.status})`);

        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const grid = this.querySelector('.byob__grid');
        const existingIds = new Set(this.cards.map((card) => card.dataset.productId));
        const newCards = Array.from(parsed.querySelectorAll('[data-byob-card]')).filter((card) => {
          const productId = card.dataset.productId;
          if (!productId || existingIds.has(productId)) return false;
          existingIds.add(productId);
          return true;
        });
        newCards.forEach((card) => {
          if (deferRender) card.hidden = true;
          grid?.append(card);
        });
        this.cards = Array.from(this.querySelectorAll('[data-byob-card]'));
        this.addHealthInterestFilters(newCards);

        const nextButton = parsed.querySelector('[data-byob-more]');
        if (nextButton?.dataset.nextUrl) {
          button.dataset.nextUrl = nextButton.dataset.nextUrl;
          if (!deferRender) {
            button.disabled = false;
            button.textContent = 'View more';
          }
        } else {
          button.remove();
        }

        if (!deferRender) this.render({ filter: true });
        return Boolean(nextButton?.dataset.nextUrl);
      }

      addHealthInterestFilters(cards) {
        const select = this.querySelector('[data-byob-interest]');
        if (!select) return;
        const interests = [...new Set(cards.flatMap((card) => this.cardHealthInterests(card)))]
          .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }));

        interests.forEach((interest) => {
          if ([...select.options].some((option) => option.value === interest)) return;
          const option = document.createElement('option');
          option.value = interest;
          option.textContent = interest;
          const later = [...select.options].find((entry) => entry.value && entry.value.localeCompare(interest, undefined, { sensitivity: 'base' }) > 0);
          if (later) later.before(option);
          else select.append(option);
        });
      }

      cardSearchText(card) {
        const cached = this.searchText.get(card);
        if (cached) return cached;

        const parts = [
          card.dataset.productTitle || '',
          ...this.cardHealthInterests(card),
          ...Array.from(card.querySelectorAll('.product-good-for li'), (item) => item.textContent),
          ...Array.from(card.querySelectorAll('[data-byob-variant-select] option'), (option) => option.textContent),
        ];
        const text = parts.join(' ').replace(/\s+/g, ' ').toLowerCase();
        this.searchText.set(card, text);
        return text;
      }

      applyFilter() {
        let visibleCards = 0;
        const query = (this.searchQuery || '').trim().toLowerCase();
        const terms = query.split(/\s+/).filter(Boolean);

        this.cards.forEach((card) => {
          const matchesType = this.cardMatchesConcern(card);
          const searchText = terms.length ? this.cardSearchText(card) : '';
          const matchesSearch = terms.every((term) => searchText.includes(term));
          const matches = matchesType && matchesSearch;
          if (card.hidden !== !matches) card.hidden = !matches;
          if (matches) visibleCards += 1;
        });

        const interestSelect = this.querySelector('[data-byob-interest]');
        if (interestSelect) {
          const nextValue = this.routineConcerns?.length ? '' : this.activeType;
          if (interestSelect.value !== nextValue) interestSelect.value = nextValue;
        }

        if (this.filterEmptyElement) {
          const filtering = Boolean(this.activeType || this.routineConcerns?.length || terms.length);
          const moreRemain = Boolean(filtering && this.querySelector('[data-byob-more]'));
          this.filterEmptyElement.hidden = !filtering || visibleCards > 0 || this.isLoadingCollection || moreRemain;
          if (!this.filterEmptyElement.hidden) {
            this.filterEmptyElement.textContent = terms.length ? 'No products match your search.' : 'No products for this.';
          }
        }
      }

      renderTiers(quantity, currentTier, subscriptionPercent) {
        if (!this.tierList) return;
        if (!this.bundleDiscountsEnabled()) {
          this.tierList.hidden = true;
          this.tierList.replaceChildren();
          return;
        }

        this.tierList.hidden = false;
        this.tierList.replaceChildren(
          ...this.config.tiers.map((tier) => {
            const item = document.createElement('div');
            item.className = 'byob-tier';
            if (quantity >= tier.quantity) item.classList.add('is-unlocked');
            if (currentTier && currentTier.quantity === tier.quantity) item.classList.add('is-active');

            const threshold = document.createElement('span');
            threshold.textContent = `Add ${tier.quantity}+ for`;
            const discount = document.createElement('strong');
            discount.textContent = `${this.combinedPercent(subscriptionPercent, tier.percent)}% off`;
            item.append(threshold, discount);
            return item;
          })
        );
      }

      combinedPercent(subscriptionPercent, bundlePercent) {
        const subscription = Number(subscriptionPercent) || 0;
        const bundle = Number(bundlePercent) || 0;
        if (!(subscription > 0)) return bundle;
        if (!(bundle > 0)) return subscription;
        const remaining = (1 - subscription / 100) * (1 - bundle / 100);
        return Math.round((1 - remaining) * 100);
      }

      renderMeter(quantity, nextTier, subscriptionPercent) {
        if (!this.meter || !this.meterFill) return;
        if (!this.bundleDiscountsEnabled()) {
          this.meter.hidden = true;
          return;
        }

        this.meter.hidden = false;
        const topTier = this.config.tiers[this.config.tiers.length - 1];
        const goal = nextTier?.quantity || topTier?.quantity || 1;
        const value = Math.min(quantity, goal);
        const ratio = goal > 0 ? value / goal : 0;
        this.meterFill.style.transform = `scaleX(${ratio})`;
        this.meter.setAttribute('aria-valuenow', String(value));
        this.meter.setAttribute('aria-valuemax', String(goal));
        const label = nextTier
          ? `${value} of ${goal} items toward ${this.combinedPercent(subscriptionPercent, nextTier.percent)}% off`
          : `${value} items. Top discount unlocked.`;
        this.meter.setAttribute('aria-valuetext', label);
      }

      rememberOtherCartTotal(cart) {
        const bundleId = this.appendBundleId();
        const counted = bundleId
          ? (cart.items || []).filter((item) => item.properties?._byob_bundle_id === bundleId)
          : [];
        const countedTotal = counted.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
        this.otherCartTotal = Math.max(0, (cart.total_price || 0) - countedTotal);
      }

      shippingState(estimatedTotal, quantity, minimumItems) {
        const threshold = Number(this.config.freeShippingThreshold) || 0;
        const cartTotal = estimatedTotal + (Number(this.otherCartTotal) || 0);
        const enabled = Boolean(this.config.freeShippingEnabled) && threshold > 0 && cartTotal > 0 && quantity >= minimumItems;
        if (!enabled) return { visible: false, qualified: false, text: '' };
        if (cartTotal >= threshold) return { visible: true, qualified: true, text: '+ free shipping' };
        return {
          visible: true,
          qualified: false,
          text: `Add ${this.formatMoney(threshold - cartTotal)} for free shipping`,
        };
      }

      catalogSubscriptionPercent() {
        if (this.catalogSubscriptionRate != null) return this.catalogSubscriptionRate;

        const percents = [];
        for (const card of this.cards) {
          const variant = this.getSelectedVariant(card);
          const price = variant?.price || 0;
          const subscriptionPrice = Number(variant?.subscriptionPrice) || 0;
          if (!variant?.sellingPlanId || price <= 0 || subscriptionPrice <= 0 || subscriptionPrice >= price) continue;
          percents.push(Math.round(((price - subscriptionPrice) / price) * 100));
          if (percents.length >= 12) break;
        }

        this.catalogSubscriptionRate = percents.length && percents.every((value) => value === percents[0]) ? percents[0] : 0;
        return this.catalogSubscriptionRate;
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this.onSearchResize);
        this.searchMedia?.removeEventListener('change', this.onSearchResize);
        this.searchHeaderObserver?.disconnect();
        if (this.searchPinFrame) cancelAnimationFrame(this.searchPinFrame);
      }

      trackSearchPin() {
        if (!this.searchMedia?.matches) {
          this.pinSearch();
          return;
        }
        if (this.searchPinFrame) cancelAnimationFrame(this.searchPinFrame);
        const until = performance.now() + 280;
        const step = (now) => {
          this.pinSearch();
          this.searchPinFrame = now < until ? requestAnimationFrame(step) : 0;
        };
        this.searchPinFrame = requestAnimationFrame(step);
      }

      pinSearch() {
        if (!this.searchBar) return;
        if (!this.searchMedia?.matches) {
          this.searchBar.style.top = '';
          return;
        }
        const header = document.querySelector('.header-container') || document.querySelector('.section-header');
        const top = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
        this.searchBar.style.top = `${top}px`;
      }

      toggleSheet() {
        if (!this.sheet || this.sheetMedia?.matches) return;
        if (this.sheet.open) this.sheet.close();
        else this.sheet.showModal();
        this.syncSheetToggle();
      }

      syncSheetToggle() {
        if (!this.dockToggle) return;
        this.dockToggle.setAttribute('aria-expanded', this.sheet?.open ? 'true' : 'false');
      }

      renderDock({ quantity, percent, nextTier, minimumItems, subscriptionPercent, retailSubtotal, estimatedTotal, shipping }) {
        if (!this.dock) return;

        this.dock.hidden = false;
        this.classList.toggle('is-docked', true);

        const combined = this.bundleDiscountsEnabled()
          ? (percent > 0 ? this.combinedPercent(subscriptionPercent, percent) : 0)
          : (Number(subscriptionPercent) || 0);
        const productLabel = quantity === 1 ? '1 product' : `${quantity} products`;
        if (this.dockOffer) {
          if (quantity > 0 && combined > 0) this.dockOffer.textContent = `${productLabel} · ${combined}% off`;
          else if (quantity > 0) this.dockOffer.textContent = productLabel;
          else if (!this.bundleDiscountsEnabled() && combined > 0) this.dockOffer.textContent = `Save ${combined}% on each product`;
          else this.dockOffer.textContent = 'Your bundle';
        }
        if (this.dockCompare) {
          this.dockCompare.hidden = !(retailSubtotal > estimatedTotal);
          this.dockCompare.textContent = this.formatMoney(retailSubtotal);
        }
        if (this.dockTotal) this.dockTotal.textContent = this.formatMoney(estimatedTotal);
        if (this.dockNudge) {
          const nudge = this.nextStepText(quantity, nextTier, minimumItems, subscriptionPercent);
          this.dockNudge.hidden = !nudge;
          this.dockNudge.textContent = nudge;
        }
        if (this.dockShipping) {
          this.dockShipping.hidden = !shipping.visible;
          this.dockShipping.textContent = shipping.text;
        }
      }

      syncDockHeight() {
        if (!this.dock || this.dock.hidden || getComputedStyle(this.dock).display === 'none') return;
        this.style.setProperty('--byob-dock-height', `${this.dock.offsetHeight}px`);
      }

      nextStepText(quantity, nextTier, minimumItems, subscriptionPercent) {
        const minimumRemaining = Math.max(Number(minimumItems || 0) - quantity, 0);
        if (!this.bundleDiscountsEnabled()) {
          if (minimumRemaining <= 0) return '';
          const offer = Number(subscriptionPercent) || 0;
          if (quantity === 0 && offer > 0) return 'Cancel anytime';
          return `Add ${minimumRemaining} more ${minimumRemaining === 1 ? 'item' : 'items'}`;
        }
        const reward = nextTier ? this.combinedPercent(subscriptionPercent, nextTier.percent) : 0;
        if (minimumRemaining > 0) {
          const extra = reward > 0 ? ` for ${reward}% off` : '';
          return `Add ${minimumRemaining} more ${minimumRemaining === 1 ? 'item' : 'items'}${extra}`;
        }
        if (!nextTier || !(reward > 0)) return '';
        const remaining = nextTier.quantity - quantity;
        return `Add ${remaining} more ${remaining === 1 ? 'item' : 'items'} for ${reward}% off`;
      }

      dockButtonLabel(remaining) {
        if (this.isSubmitting) return 'Adding bundle...';
        if (this.appendBundleId() && this.cartBundleVariantIds && this.newBundleItems().length === 0) return 'Add 1 more';
        if (remaining > 0) return `Add ${remaining} more`;
        return 'Add Bundle to Cart';
      }

      renderProgress(quantity, tier, nextTier, minimumItems, subscriptionPercent) {
        if (!this.progress) return;
        if (!this.bundleDiscountsEnabled()) {
          this.progress.hidden = true;
          this.progress.textContent = '';
          return;
        }

        const firstTier = this.config.tiers[0];
        let message = '';

        if (quantity === 0) {
          const offer = firstTier ? this.combinedPercent(subscriptionPercent, firstTier.percent) : 0;
          message = firstTier
            ? `Add ${firstTier.quantity} items for ${offer}% off.`
            : `Add ${minimumItems} items to start a bundle.`;
        } else if (!tier) {
          const target = firstTier?.quantity || minimumItems;
          const remaining = Math.max(target - quantity, 1);
          const offer = firstTier ? this.combinedPercent(subscriptionPercent, firstTier.percent) : 0;
          message = offer > 0
            ? `Add ${remaining} more ${remaining === 1 ? 'item' : 'items'} for ${offer}% off.`
            : `Add ${remaining} more ${remaining === 1 ? 'item' : 'items'}.`;
        } else if (nextTier) {
          const remaining = nextTier.quantity - quantity;
          const offer = this.combinedPercent(subscriptionPercent, nextTier.percent);
          message = `Add ${remaining} more ${remaining === 1 ? 'item' : 'items'} for ${offer}% off.`;
        }

        const tierQuantity = tier ? tier.quantity : 0;
        const justUnlocked = this.announcedTier != null && tierQuantity > this.announcedTier;
        this.progress.textContent = message;
        if (justUnlocked) {
          this.progress.classList.remove('is-just-unlocked');
          void this.progress.offsetWidth;
          this.progress.classList.add('is-just-unlocked');
        }
        this.announcedTier = tierQuantity;
        this.progress.hidden = !message;
      }

      renderHeading(quantity, percent, subscriptionPercent) {
        const showCount = quantity > 0;
        const combined = this.bundleDiscountsEnabled()
          ? (percent > 0 ? this.combinedPercent(subscriptionPercent, percent) : 0)
          : (Number(subscriptionPercent) || 0);
        const showDiscount = combined > 0;

        if (this.countElement) {
          this.countElement.hidden = !showCount;
          this.countElement.textContent = showCount ? ` (${quantity})` : '';
        }

        if (this.discountElement) {
          this.discountElement.hidden = !showDiscount;
          this.discountElement.textContent = showDiscount ? `${combined}% off` : '';
        }
      }

      setupStarter() {
        if (!this.starter || !this.starterList) return;

        const target = this.config.tiers[this.config.tiers.length - 1]?.quantity || 4;
        const bestSellers = [];
        const others = [];
        this.cards.forEach((card) => {
          const badge = card.querySelector('.byob-card__badge')?.textContent || '';
          if (/best seller/i.test(badge)) bestSellers.push(card);
          else others.push(card);
        });

        this.starterCards = [...bestSellers, ...others].slice(0, target);
        if (this.starterCards.length < 2) {
          this.starter.hidden = true;
          return;
        }

        const topTier = this.config.tiers[this.config.tiers.length - 1];
        if (this.starterTitle) this.starterTitle.textContent = `Start with these ${this.starterCards.length}`;
        if (this.starterCopy) {
          const offer = this.bundleDiscountsEnabled() && topTier
            ? this.combinedPercent(this.catalogSubscriptionPercent(), topTier.percent)
            : this.catalogSubscriptionPercent();
          this.starterCopy.textContent = offer > 0
            ? `Popular picks to reach ${offer}% off. Swap anything after you add them.`
            : 'Popular picks to get started. Swap anything after you add them.';
        }

        this.starterList.replaceChildren(
          ...this.starterCards.map((card) => {
            const item = document.createElement('li');
            item.className = 'byob-starter__item';
            item.dataset.productId = card.dataset.productId || '';

            const image = document.createElement('img');
            image.alt = '';
            image.width = 72;
            image.height = 72;
            image.src = card.dataset.productImage || card.querySelector('.byob-card__media img')?.currentSrc || '';
            if (!image.src) image.hidden = true;

            const title = document.createElement('p');
            title.className = 'byob-starter__name';
            title.textContent = card.dataset.productTitle || '';

            const price = document.createElement('p');
            price.className = 'byob-starter__price';
            price.dataset.byobStarterPrice = '';

            item.append(image, title, price);
            return item;
          })
        );
        this.starter.hidden = false;
      }

      renderStarter() {
        if (!this.starter || !this.starterCards.length) return;

        let missing = 0;
        this.starterCards.forEach((card) => {
          const added = this.selectedProductIds.has(card.dataset.productId);
          if (!added) missing += 1;
          const item = this.starterList?.querySelector(`[data-product-id="${CSS.escape(card.dataset.productId)}"]`);
          if (!item) return;
          item.classList.toggle('is-added', added);
          const price = item.querySelector('[data-byob-starter-price]');
          const cardPrice = card.querySelector('[data-byob-price]');
          if (price && cardPrice) price.textContent = cardPrice.textContent;
        });

        if (!this.starterButton) return;
        this.starterButton.disabled = missing === 0;
        if (missing === 0) this.starterButton.textContent = 'In your bundle';
        else if (missing === this.starterCards.length) this.starterButton.textContent = 'Add this set';
        else this.starterButton.textContent = 'Add the rest';
      }

      addStarterSet() {
        const selected = new Set(Array.from(this.items.values(), (item) => item.productId));
        let added = false;

        this.starterCards.forEach((card) => {
          const variant = this.getSelectedVariant(card);
          if (!variant?.available || selected.has(variant.productId)) return;
          this.items.set(variant.key, { ...variant, quantity: 1 });
          selected.add(variant.productId);
          added = true;
        });

        if (!added) return;
        this.setStatus('');
        this.render();
      }

      renderSummary(percent = 0) {
        if (!this.summaryItems || !this.emptyState) return;

        const items = Array.from(this.items.values());
        if (items.length === 0 && !this.bundleDiscountsEnabled()) {
          const offer = this.catalogSubscriptionPercent();
          this.emptyState.textContent = offer > 0
            ? `Save ${offer}% on each product. Cancel anytime.`
            : 'Your bundle is empty.';
        } else if (items.length === 0) {
          this.emptyState.textContent = 'Your bundle is empty.';
        }
        this.emptyState.hidden = items.length > 0;
        this.summaryItems.hidden = items.length === 0;
        this.summaryItems.replaceChildren(
          ...items.map((item) => {
            const row = document.createElement('li');
            row.className = 'byob-summary__item';
            row.dataset.byobKey = item.key;

            const inCart = this.isInCartBundle(item.variantId);
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.className = 'byob-summary__remove';
            remove.dataset.byobRemove = '';
            remove.setAttribute('aria-label', `Remove ${item.productTitle}`);
            remove.textContent = '×';

            const image = document.createElement('div');
            image.className = 'byob-summary__image';
            if (item.image) {
              const img = document.createElement('img');
              img.src = item.image;
              img.alt = '';
              image.append(img);
            }

            const body = document.createElement('div');
            body.className = 'byob-summary__body';

            const details = document.createElement('div');
            details.className = 'byob-summary__details';
            const moment = item.moment || this.momentLabelForProduct(item.productId);
            if (moment) {
              const momentLabel = document.createElement('p');
              momentLabel.className = 'byob-summary__moment';
              momentLabel.textContent = moment;
              details.append(momentLabel);
            }

            const title = document.createElement('p');
            title.className = 'byob-summary__title';
            title.textContent = item.productTitle;
            details.append(title);

            if (item.variantTitle && item.variantTitle !== 'Default Title') {
              const variant = document.createElement('p');
              variant.className = 'byob-summary__variant';
              variant.textContent = item.variantTitle;
              details.append(variant);
            }

            if (inCart) {
              const note = document.createElement('p');
              note.className = 'byob-summary__incart';
              note.textContent = 'In cart';
              details.append(note);
            }

            const prices = document.createElement('div');
            prices.className = 'byob-summary__prices';
            const price = document.createElement('p');
            price.className = 'byob-summary__price';
            const base = this.basePrice(item);
            const discountedPrice = this.applyPercent(base, percent);
            price.textContent = this.formatMoney(discountedPrice);
            if (discountedPrice < item.price) price.classList.add('is-sale');
            prices.append(price);

            if (discountedPrice < item.price) {
              const compare = document.createElement('s');
              compare.className = 'byob-summary__compare';
              compare.textContent = this.formatMoney(item.price);
              prices.append(compare);
            }

            body.append(details, prices);
            if (!inCart) row.append(remove);
            row.append(image, body);
            return row;
          })
        );
      }

      renderCards(percent) {
        this.cards.forEach((card) => {
          const saved = Array.from(this.items.values()).find((item) => item.productId === card.dataset.productId);
          const select = card.querySelector('[data-byob-variant-select]');
          if (saved && select && select.value !== String(saved.variantId)) {
            const match = [...select.options].some((option) => option.value === String(saved.variantId));
            if (match) select.value = String(saved.variantId);
          }
          const variant = this.getSelectedVariant(card);
          if (!variant) return;

          const added = this.selectedProductIds.has(card.dataset.productId);
          const inCart = this.productIsInCartBundle(card.dataset.productId);
          const state = this.cardState.get(card);
          if (state && state.percent === percent && state.added === added && state.inCart === inCart && state.variantId === variant.variantId) return;
          if (!state && !added && percent === 0 && this.basePrice(variant) === variant.price) {
            this.cardState.set(card, { percent: 0, added: false, variantId: variant.variantId });
            return;
          }

          this.renderCard(card, percent, variant, added);
        });
      }

      renderCard(card, percent, knownVariant, knownAdded) {
        const variant = knownVariant || this.getSelectedVariant(card);
        if (!variant) return;

        const productAdded = knownAdded ?? this.productIsSelected(card.dataset.productId);
        const price = card.querySelector('[data-byob-price]');
        const compare = card.querySelector('[data-byob-compare]');
        const offer = card.querySelector('[data-byob-offer]');
        const sizeLabel = card.querySelector('[data-byob-size]');
        const addButton = card.querySelector('[data-byob-add]');
        const removeButton = card.querySelector('[data-byob-remove]');
        const variantSelect = card.querySelector('[data-byob-variant-select]');
        const base = this.basePrice(variant);
        const discountedPrice = this.applyPercent(base, percent);

        card.classList.toggle('is-added', productAdded);
        if (variantSelect) variantSelect.disabled = productAdded;
        if (price) {
          price.textContent = this.formatMoney(discountedPrice);
          price.classList.toggle('is-sale', discountedPrice < variant.price);
        }
        if (compare) {
          compare.hidden = discountedPrice >= variant.price;
          compare.textContent = this.formatMoney(variant.price);
        }
        if (offer) {
          const rate = variant.price > 0 && discountedPrice < variant.price
            ? Math.round(((variant.price - discountedPrice) / variant.price) * 100)
            : 0;
          offer.hidden = !(rate > 0);
          offer.textContent = rate > 0 ? `${rate}% off` : '';
        }
        if (sizeLabel) {
          sizeLabel.hidden = !variant.variantSize;
          sizeLabel.textContent = variant.variantSize ? `(${variant.variantSize})` : '';
        }
        if (addButton) {
          const title = card.dataset.productTitle || 'product';
          addButton.disabled = productAdded || !variant.available;
          if (productAdded) {
            addButton.textContent = 'Added';
            addButton.setAttribute('aria-label', `${title} added to bundle`);
          } else if (!variant.available) {
            addButton.textContent = 'Sold out';
            addButton.setAttribute('aria-label', `${title} sold out`);
          } else {
            addButton.textContent = 'Add to bundle';
            addButton.setAttribute('aria-label', `Add ${title} to bundle`);
          }
        }
        const inCart = this.productIsInCartBundle(card.dataset.productId);
        if (removeButton) removeButton.hidden = !productAdded || inCart;
        this.cardState.set(card, { percent, added: productAdded, variantId: variant.variantId, inCart });
      }

      isInCartBundle(variantId) {
        return Boolean(this.cartBundleVariantIds?.has(String(variantId)));
      }

      productIsInCartBundle(productId) {
        return Array.from(this.items.values()).some(
          (item) => item.productId === productId && this.isInCartBundle(item.variantId)
        );
      }

      newBundleItems() {
        if (!this.appendBundleId() || !this.cartBundleVariantIds) return Array.from(this.items.values());
        return Array.from(this.items.values()).filter((item) => !this.cartBundleVariantIds.has(String(item.variantId)));
      }

      purchaseButtonLabel(quantity, subscriptionTotal) {
        if (this.isSubmitting) return 'Adding bundle...';
        if (this.appendBundleId() && this.cartBundleVariantIds && this.newBundleItems().length === 0) return 'Add 1 more product';
        const remaining = Math.max(Number(this.config.minimumItems || 0) - quantity, 0);
        if (remaining > 0) return `Add ${remaining} more ${remaining === 1 ? 'item' : 'items'}`;
        if (quantity && this.bundleHasSubscription()) return 'Add Bundle to Cart';
        return this.config.submitLabel || 'Add Bundle to Cart';
      }

      bundleHasSubscription() {
        return Array.from(this.items.values()).some((item) => item.sellingPlanId);
      }

      renderOnceOption(quantity, onceTotal, canSubmit) {
        const subscriptionBundle = Boolean(quantity) && this.bundleHasSubscription() && !this.isSubmitting;
        const showOnce = subscriptionBundle && !this.cartBundleSubscription;
        if (this.onceButton) {
          this.onceButton.hidden = !showOnce;
          this.onceButton.disabled = !canSubmit;
          this.onceButton.textContent = `Buy it once for ${this.formatMoney(onceTotal)}`;
        }
        if (!this.subscribeNote) return;
        const planName = Array.from(this.items.values()).find((item) => item.sellingPlanName)?.sellingPlanName;
        this.subscribeNote.hidden = !subscriptionBundle;
        this.subscribeNote.textContent = this.subscriptionNote(planName);
      }

      subscriptionNote(planName) {
        if (!planName) return 'Subscription. Cancel anytime.';
        const frequency = planName.replace(/^delivery\s+/i, '');
        const cadence = `${frequency.charAt(0).toLowerCase()}${frequency.slice(1)}`;
        return `Subscription · delivers ${cadence}. Cancel anytime.`;
      }

      renderMoney(element, cents) {
        if (element) element.textContent = this.formatMoney(cents);
      }

      appendBundleId() {
        const bundleId = new URL(window.location.href).searchParams.get('byob_bundle_id') || '';
        return bundleId.startsWith('byob-') ? bundleId : '';
      }

      async submitBundle(purchaseType = 'subscription') {
        const quantity = this.getTotalQuantity();
        const appending = Boolean(this.appendBundleId());
        if (purchaseType === 'once' && this.cartBundleSubscription) return;
        const belowMinimum = quantity < Number(this.config.minimumItems || 0);
        if (this.isSubmitting || quantity < 1 || (!appending && belowMinimum)) return;

        this.isSubmitting = true;
        this.render();
        this.setStatus('');

        const bundleId = this.appendBundleId() || `byob-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        let selectedItems = Array.from(this.items.values());
        try {
          const currentCart = await fetch('/cart.js').then((response) => response.json());
          const existingVariantIds = new Set(
            (currentCart.items || [])
              .filter((item) => {
                const lineBundleId = item.properties?._byob_bundle_id || '';
                if (!String(lineBundleId).startsWith('byob-')) return false;
                return appending ? lineBundleId === bundleId : true;
              })
              .map((item) => String(item.variant_id))
          );
          selectedItems = selectedItems.filter((item) => !existingVariantIds.has(String(item.variantId)));
        } catch (error) {
          console.error(error);
        }
        if (!selectedItems.length) {
          this.isSubmitting = false;
          this.render();
          this.setStatus(appending ? 'Those products are already in this bundle.' : 'Those products are already in your cart.');
          return;
        }
        const body = {
          items: selectedItems.map((item) => {
            const line = {
              id: item.variantId,
              quantity: item.quantity,
              properties: {
                _byob_bundle_id: bundleId,
                _byob_bundle_name: this.config.bundleName || 'Build Your Own Bundle',
                _byob_source: 'bundle-builder-v1',
                _byob_version: '1',
              },
            };
            if (purchaseType === 'subscription' && item.sellingPlanId) {
              line.selling_plan = Number(item.sellingPlanId);
            }
            return line;
          }),
          sections_url: window.location.pathname,
        };

        if (cart?.getSectionsToRender) {
          body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
          cart.setActiveElement?.(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body: JSON.stringify(body),
          });
          const parsedState = await response.json();

          if (!response.ok || parsedState.status || parsedState.errors) {
            const message = parsedState.description || parsedState.message || parsedState.errors || 'There was an error adding your bundle.';
            this.setStatus(typeof message === 'string' ? message : 'There was an error adding your bundle.');
            return;
          }

          this.rememberOtherCartTotal(parsedState);
          this.sheet?.close();
          if (cart?.renderContents) {
            cart.classList.remove('is-empty');
            cart.renderContents(parsedState);
          } else {
            window.location = window.routes.cart_url;
            return;
          }

          this.items.clear();
          if (this.appendBundleId()) {
            this.setStatus('Added to your bundle.');
            await this.hydrateFromCartBundle();
          } else {
            this.setStatus('Bundle added to cart.');
          }
        } catch (error) {
          console.error(error);
          this.setStatus('There was an error adding your bundle.');
        } finally {
          this.isSubmitting = false;
          this.render();
        }
      }

      setStatus(message) {
        if (!this.statusElement) return;
        this.statusElement.textContent = message;
      }

      formatMoney(cents) {
        const placeholderMatch = this.moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
        const placeholder = placeholderMatch ? placeholderMatch[1] : 'amount';
        const amount = (Number(cents) || 0) / 100;
        const withSeparator = (value, separator) => value.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
        let value = withSeparator(amount.toFixed(2), ',');

        if (placeholder === 'amount_no_decimals') {
          value = withSeparator(amount.toFixed(0), ',');
        } else if (placeholder === 'amount_with_comma_separator') {
          value = withSeparator(amount.toFixed(2).replace('.', ','), '.');
        } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
          value = withSeparator(amount.toFixed(0), '.');
        } else if (placeholder === 'amount_with_apostrophe_separator') {
          value = withSeparator(amount.toFixed(2), "'");
        } else if (placeholder === 'amount_no_decimals_with_space_separator') {
          value = withSeparator(amount.toFixed(0), ' ');
        } else if (placeholder === 'amount_with_space_separator') {
          value = withSeparator(amount.toFixed(2).replace('.', ','), ' ');
        } else if (placeholder === 'amount_with_period_and_space_separator') {
          value = withSeparator(amount.toFixed(2), ' ');
        }

        return placeholderMatch ? this.moneyFormat.replace(placeholderMatch[0], value) : value;
      }
    }
  );
}
